/**
 * Premium Audio Player for Shopify 
 */

let activePlayers = [];
let currentlyPlaying = null;
let durationCache = {};

class PremiumPlayer {
  constructor(container) {
    this.container = container;
    this.isCollection = container.classList.contains("collection-version");
    
    this.playBtn = container.querySelector(".play-toggle");
    this.prevBtn = container.querySelector(".prev-track");
    this.nextBtn = container.querySelector(".next-track");

    const visibleTrackList = container.querySelector(
      ".track-list:not(.fallback-track-list)"
    );

    if (visibleTrackList) {
      // Bundle product (real visible tracks)
      this.trackItems = visibleTrackList.querySelectorAll(".track-item");
    } else {
      // Single product (fallback list only)
      this.trackItems = container.querySelectorAll(
        ".fallback-track-list .track-item"
      );
    }

    this.trackInfo = container.querySelector(".track-info");
    this.waveContainer = container.querySelector(".waveform");
    this.durationElements = container.querySelectorAll(".track-duration");

    this.currentIndex = 0;
    this.audio = new Audio();
    this.audio.preload = "metadata";
    this.wavesurfer = null;

    this.init();
    activePlayers.push(this);
  }

  init() {
    this.loadTrack(0, false);
    this.bindEvents();
    
    if (!this.isCollection) {
      this.fetchAllDurations();
    }

    if (!this.isCollection && this.waveContainer && typeof WaveSurfer !== "undefined") {
      requestAnimationFrame(() => {
        if (!this.container) return;
        this.wavesurfer = WaveSurfer.create({
          container: this.waveContainer,
          media: this.audio,
          normalize: true,
          waveColor: "#4A4A4A",
          progressColor: "#5DBBFF",
          height: 60,
          barWidth: 0,
          interact: true,
          cursorWidth: 0
        });
      });
    }

    if (this.isCollection) {
      const progressContainer = this.container.querySelector(".collection-progress-container");
      const progressBar = this.container.querySelector(".collection-progress-bar");

      // Update progress visually
      this.audio.addEventListener("timeupdate", () => {
        if (this.audio.duration && progressBar) {
          const percentage = (this.audio.currentTime / this.audio.duration) * 100;
          progressBar.style.width = `${percentage}%`;
        }
      });

      // CLICK TO SEEK
      if (progressContainer) {
        progressContainer.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();

          if (!this.audio.duration) return;

          const rect = progressContainer.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          const percentage = clickX / rect.width;
          const newTime = percentage * this.audio.duration;

          this.audio.currentTime = newTime;
        });
      }
    }
  }

  fetchAllDurations() {
    const elements = Array.from(this.durationElements);

    // --- FIRST TRACK (use main audio instance) ---
    const firstEl = elements[0];
    if (firstEl) {
      this.audio.addEventListener("loadedmetadata", () => {
        const mins = Math.floor(this.audio.duration / 60);
        const secs = Math.floor(this.audio.duration % 60)
          .toString()
          .padStart(2, "0");

        const formatted = `${mins}:${secs}`;
        durationCache[this.audio.src] = formatted;
        firstEl.textContent = formatted;
      }, { once: true });
    }

    // --- REMAINING TRACKS (staggered loading) ---
    elements.slice(1).forEach((el, index) => {
      const url = el.dataset.url;
      if (!url) return;

      if (durationCache[url]) {
        el.textContent = durationCache[url];
        return;
      }

      setTimeout(() => {
        const tempAudio = new Audio();
        tempAudio.preload = "metadata";
        tempAudio.src = url;

        tempAudio.addEventListener("loadedmetadata", () => {
          const mins = Math.floor(tempAudio.duration / 60);
          const secs = Math.floor(tempAudio.duration % 60)
            .toString()
            .padStart(2, "0");

          const formatted = `${mins}:${secs}`;

          durationCache[url] = formatted;
          el.textContent = formatted;

          tempAudio.src = "";
          tempAudio.load();
        });
      }, index * 300);
    });
  }

  bindEvents() {

    // Prevent product card navigation when clicking anywhere inside the player
    this.container.addEventListener("click", (e) => {
      e.stopPropagation();
    });

    this.container.addEventListener("touchstart", (e) => {
      e.stopPropagation();
    }, { passive: true });

    // Stop propagation on specific interactive buttons
    [this.playBtn, this.prevBtn, this.nextBtn].forEach(btn => {
      if (!btn) return;

      btn.addEventListener("click", (e) => {
        e.stopPropagation();
      });

      btn.addEventListener("touchstart", (e) => {
        e.stopPropagation();
      }, { passive: true });
    });

    this.playBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (currentlyPlaying && currentlyPlaying !== this) currentlyPlaying.stop();
      
      const isPaused = this.wavesurfer ? !this.wavesurfer.isPlaying() : this.audio.paused;

      if (isPaused) {
        currentlyPlaying = this;
        const engine = this.wavesurfer || this.audio;
        engine.play()
          .then(() => {
            this.playBtn.classList.add("playing");
          })
          .catch((err) => {
            console.warn("Playback failed:", err);
            this.playBtn.classList.remove("playing");
          });
      } else {
        this.stop();
      }
    });

    if (this.nextBtn) {
      this.nextBtn.addEventListener("click", (e) => {
        e.preventDefault(); e.stopPropagation();
        this.playNextTrack();
      });
    }

    if (this.prevBtn) {
      this.prevBtn.addEventListener("click", (e) => {
        e.preventDefault(); e.stopPropagation();
        this.playPrevTrack();
      });
    }

    this.trackItems.forEach((item, index) => {
      item.addEventListener("click", (e) => {
        e.preventDefault(); e.stopPropagation();

        if (currentlyPlaying && currentlyPlaying !== this) currentlyPlaying.stop();
        const isCurrentlyActive = this.currentIndex === index && currentlyPlaying === this;
        const isPaused = this.wavesurfer ? !this.wavesurfer.isPlaying() : this.audio.paused;

        if (isCurrentlyActive && !isPaused) {
          this.stop();
          return;
        }

        this.loadTrack(index, true);
        currentlyPlaying = this;
      });
    });

    this.audio.addEventListener("ended", () => {
      const isLastTrack = this.currentIndex === this.trackItems.length - 1;

      if (this.isCollection) {
        this.stop();
        this.audio.currentTime = 0;

        const bar = this.container.querySelector(".collection-progress-bar");
        if (bar) bar.style.width = "0%";
        return;
      }

      // PRODUCT PAGE LOGIC
      if (this.trackItems.length > 1) {
        if (isLastTrack) {
          // Reset to first track WITHOUT autoplay
          this.loadTrack(0, false);
          this.audio.currentTime = 0;
          this.playBtn.classList.remove("playing");
          currentlyPlaying = null;
        } else {
          // Go to next track and autoplay
          this.playNextTrack();
        }
      } else {
        // Single-track product: just stop
        this.stop();
        this.audio.currentTime = 0;
      }
    });
  }

  loadTrack(index, shouldPlay = false) {
    const item = this.trackItems[index];
    if (!item) return;

    this.trackItems.forEach(t => t.classList.remove("active"));
    item.classList.add("active");
    this.currentIndex = index;

    if (this.isCollection) {
      const bar = this.container.querySelector(".collection-progress-bar");
      if (bar) bar.style.width = "0%";
    } else if (this.trackInfo) {
      this.trackInfo.textContent = item.dataset.title;
    }
    
    this.playBtn.classList.remove("playing");
    const trackUrl = item.dataset.url;

    if (this.wavesurfer) {
      this.wavesurfer.load(trackUrl);
      if (shouldPlay) {
        this.wavesurfer.once('ready', () => {
          this.wavesurfer.play().then(() => { this.playBtn.classList.add("playing"); });
        });
      }
    } else {
      this.audio.src = trackUrl;
      if (shouldPlay) {
        this.audio.oncanplay = async () => {
          try {
            await this.audio.play();
            this.playBtn.classList.add("playing");
          } catch (err) {
            console.log("Playback blocked:", err);
          }
          this.audio.oncanplay = null;
        };
      }
    }
  }

  playNextTrack() {
    let nextIndex = this.currentIndex + 1;
    if (nextIndex >= this.trackItems.length) nextIndex = 0;
    this.loadTrack(nextIndex, true);
  }

  playPrevTrack() {
    let prevIndex = this.currentIndex - 1;
    if (prevIndex < 0) prevIndex = this.trackItems.length - 1;
    this.loadTrack(prevIndex, true);
  }

  stop() {
    if (this.wavesurfer) {
      this.wavesurfer.pause();
    } else {
      this.audio.pause();
    }
    this.playBtn.classList.remove("playing");
    if (currentlyPlaying === this) currentlyPlaying = null;
  }

  destroy() {
    if (currentlyPlaying === this) currentlyPlaying = null;

    this.audio.pause();
    this.audio.src = "";
    this.audio.load();

    if (this.wavesurfer) {
      this.wavesurfer.destroy();
      this.wavesurfer = null;
    }
  }
}

const renderPlayers = () => {
  activePlayers.forEach(p => p.destroy());
  activePlayers = [];
  document.querySelectorAll(".premium-audio-player").forEach(p => new PremiumPlayer(p));
};

document.addEventListener("DOMContentLoaded", renderPlayers);
document.addEventListener("shopify:section:load", renderPlayers);
